import { css } from "@emotion/react";
import { FlexRowContainer } from "../../../bits/layout";
import type { AuthUser } from "@aws-amplify/auth";
import { useConfig } from "../../../hooks/useConfig";
import { MarketingText } from "./MarketingText";
import { colours } from "../../../../design/colours";
import { useMediaQuery } from "@mui/material";

const LargeScreenBackground = () => {
  const { publicPublications } = useConfig();

  return (
    <div
      css={css`
        height: 100vh;
        height: calc(100dvh - 32px);
        width: 50vw;
        width: calc(50dvw - 32px);
        margin: 16px;
        position: relative;
        overflow: hidden;
        border-radius: 16px;
      `}
    >
      <div
        css={({ colours }) => css`
          position: absolute;
          width: 500px;
          @media (max-width: 1200px) {
            width: 400px;
          }
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          gap: 16px;
          padding: 20px 24px 32px;
          border-radius: 36px;
          color: ${colours.primary.mid.dark20};
          background-color: ${colours.uIGrey.light._5};
          z-index: 1;
          box-shadow:
            0 0 4096px 428px rgba(9, 65, 161, 0.5),
            0 0 4096px 428px rgba(15, 53, 131, 0.25);
        `}
      >
        <MarketingText />
      </div>
      <div
        css={css`
          @media (max-width: 1000px) {
            display: none;
          }
          position: relative;
          overflow: hidden;
          border-radius: 16px;
          background: linear-gradient(135deg, #fdfdfb 0%, #e0ded6 100%);
          height: 100%;
        `}
      >
        <div
          css={css`
            display: grid;
            position: relative;
            left: -30%;
            top: -30%;
            grid-template-columns: repeat(3, 1fr);
            grid-template-rows: repeat(3, 1fr);
            grid-auto-flow: column;
            height: 150%;
            width: 150%;
            @media (max-width: 1600px) {
              width: 1300px;
              height: 1580px;
            }
            gap: 8%;
            transform: rotate(-14deg);
            background: linear-gradient(to bottom, #0052cd, #1a1f4c);
            box-shadow:
              0 0 4096px 128px rgba(9, 64, 161, 0.25),
              0 0 4096px 128px rgba(15, 53, 131, 0.25);
          `}
        >
          {publicPublications
            ?.filter((_, index) => index < 9)
            .map(
              (
                pub: { title: string; imageObjectUrl: string },
                index: number,
              ) => (
                <div
                  css={css`
                    position: relative;
                  `}
                  key={pub.title}
                >
                  <img
                    css={css`
                      ${index === 3 || index === 4 || index === 5
                        ? `position: absolute;
                            top: 50%;`
                        : ""}
                    `}
                    src={pub.imageObjectUrl}
                    alt={pub.title}
                    width={"100%"}
                    height={"100%"}
                  />
                </div>
              ),
            )}
        </div>
      </div>
    </div>
  );
};

const MobileBackground = () => {
  const { publicPublications } = useConfig();

  return (
    <div
      css={css`
        height: 100vh;
        position: absolute;
        top: -10%;
        left: -30%;
        @media (max-width: 700px) {
          width: 1000px;
        }
      `}
    >
      <div
        css={css`
          height: 140%;
          overflow: hidden;
        `}
      >
        <div
          css={css`
            display: grid;

            grid-template-columns: repeat(3, 1fr);
            grid-template-rows: repeat(3, 1fr);
            grid-auto-flow: column;
            height: 120%;
            width: 120%;
            gap: 8%;
            transform: rotate(-14deg);
            background: linear-gradient(to bottom, #0052cd, #1a1f4c);
            box-shadow:
              0 0 4096px 128px rgba(9, 64, 161, 0.25),
              0 0 4096px 128px rgba(15, 53, 131, 0.25);
          `}
        >
          {publicPublications
            ?.filter((_, index) => index < 9)
            .map(
              (
                pub: { title: string; imageObjectUrl: string },
                index: number,
              ) => (
                <div
                  css={css`
                    position: relative;
                  `}
                >
                  <img
                    css={css`
                      ${index === 3 || index === 4 || index === 5
                        ? `position: absolute;
                            top: 50%;`
                        : ""}
                    `}
                    key={index}
                    src={pub.imageObjectUrl}
                    alt={pub.title}
                    width={"100%"}
                    height={"100%"}
                  />
                </div>
              ),
            )}
        </div>
      </div>
    </div>
  );
};

const AuthenticatorBackground = ({
  user,
  children,
}: {
  user?: AuthUser;
  children: React.ReactNode;
}) => {
  const isMobile = useMediaQuery(`(max-width: 1000px)`);

  return (
    <FlexRowContainer
      css={css`
        height: 100vh;
        height: 100dvh;
        ${!user && isMobile
          ? `
            width: 100vw;
            width: 100dvw;
            position: relative;
            background: linear-gradient(135deg, #fdfdfb 0%, #e0ded6 100%);
            z-index: 0;
            overflow: scroll;`
          : "justify-content: center;"}
      `}
    >
      {!user && !isMobile && <LargeScreenBackground />}
      {!user && isMobile && <MobileBackground />}
      <div
        css={css`
          width: ${user ? "100vw" : "50vw"};
          width: ${user ? "100dvw" : "50dvw"};
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          ${!user && isMobile
            ? `
            width: 100vw;
            width: 100dvw;
            padding: 32px;
            box-shadow:
              0 0 4096px 128px rgba(9, 64, 161, 0.25),
              0 0 4096px 128px rgba(15, 53, 131, 0.25);
            `
            : "justify-content: center;"}
        `}
      >
        <div
          css={css`
            width: 100%;
            @media (max-width: 1000px) {
              ${user
                ? ""
                : ` 
            z-index: 1;
            margin: 16px;
            border-radius: 32px;
            background-color: ${colours.uIGrey.light._5};
            border: 1px solid rgba(198, 200, 210, 1);
            padding-bottom: 32px;
            box-shadow:
              0 0 4096px 128px rgba(9, 64, 161, 0.5),
              0 0 4096px 1208px rgba(15, 53, 131, 0.5);
            `};
            }
          `}
        >
          {children}
        </div>
        {!user && isMobile && (
          <div
            css={css`
              z-index: 1;
            `}
          >
            <MarketingText
              css={({ colours }) => css`
                margin: 24px;
                background-color: ${colours.uIGrey.light._5};
                border: 1px solid rgba(198, 200, 210, 1);
                flex-grow: 0;
                padding: 32px;
                border-radius: 32px;
              `}
            />
          </div>
        )}
      </div>
    </FlexRowContainer>
  );
};

export default AuthenticatorBackground;
